import { memo, useMemo } from "react";
import { Button } from "../../../components";
import { useAuthenticationService } from "../../../services/authentication.service";
import { roles } from "../../../utils/constants";
import PropTypes from "prop-types";

const CentresWithUnreconciledBalance = memo(({ centres = [], onNotifyCentres }) => {
  const authenticationService = useAuthenticationService();
  const isAdmin = useMemo(() => {
    return authenticationService.isInAnyRole(roles.ADMINS);
  }, [authenticationService]);

  function navigateToCentre(centreId) {
    window.location.href = `/centre-banking/${centreId}`;
  }

  function sendMessageAlert() {
    onNotifyCentres();
  }

  // Sort centres alphabetically by centreName
  const sortedCentres = useMemo(() => {
    return [...centres].sort((a, b) => a.centreName.localeCompare(b.centreName));
  }, [centres]);

  // Format phone number to NZ format: (07) 123 1234
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";

    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, "");

    // If it starts with 0, use as is, otherwise add 0
    const withZero = digits.startsWith("0") ? digits : `0${digits}`;

    // Format as (07) 123 1234
    if (withZero.length >= 7) {
      const areaCode = withZero.slice(0, 2);
      const firstPart = withZero.slice(2, 5);
      const secondPart = withZero.slice(5, 9);
      const remaining = withZero.slice(9);

      let formatted = `(${areaCode}) ${firstPart} ${secondPart}`;
      if (remaining) {
        formatted += ` ${remaining}`;
      }
      return formatted;
    }

    return withZero;
  };

  if (!sortedCentres || sortedCentres.length === 0) {
    return null;
  }

  return (
    <div style={{paddingTop: "15px"}}>
      {isAdmin && (
        <span style={{ margin: "15px", width: "100%" }}>
          <Button onClick={sendMessageAlert} width="calc(100% - 30px)" text="Send Email Notifications" />
        </span>
      )}
      <div className="centres-list-container">
        <div className="centres-compact-list">

          {sortedCentres.map((centre) => (
            <div key={centre.id} className="centre-compact-item">
              <div className="centre-compact-header">
                <a className="centre-compact-name" onClick={navigateToCentre.bind(null, centre.ids?.join(","))} title="Click to view centre banking">
                  {centre.centreName}
                </a>
                <span className="centre-compact-amount">
                  {centre.totalUnreconciled?.toLocaleString("en-NZ", { style: "currency", currency: "NZD" })}
                </span>
              </div>
              <div className="centre-compact-contact">
                <div className="centre-compact-meta">
                  {centre.phoneNumber && <span className="centre-compact-phone">📞 {formatPhoneNumber(centre.phoneNumber)}</span>}
                  <span className="centre-compact-ece">ECE: {centre.eceNumbers?.join(",")}</span>
                </div>
                {centre.email && (
                  <a href={`mailto:${centre.email}`} className="centre-compact-email" title="Click to open in Outlook">
                    📧 {centre.email}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

CentresWithUnreconciledBalance.displayName = "CentresWithUnreconciledBalance";

CentresWithUnreconciledBalance.propTypes = {
  centres: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      centreName: PropTypes.string.isRequired,
      eceNumber: PropTypes.number.isRequired,
      cashBatchesToReconcile: PropTypes.number,
      cashTotalToReconcile: PropTypes.number,
      eftposBatchesToReconcile: PropTypes.number,
      eftposTotalToReconcile: PropTypes.number,
      eftposTransactionsToReconcile: PropTypes.number,
      email: PropTypes.string,
      phoneNumber: PropTypes.string,
      totalUnreconciled: PropTypes.number,
    })
  ),
  onNotifyCentres: PropTypes.func.isRequired,
};

export default CentresWithUnreconciledBalance;
